<template>
  <div class="spl-report-list-table">
    <table v-if="!isLoading">
      <thead>
        <tr class="spl-parcel-table__head">
          <th>Delete Report</th>
          <th
            v-for="(header, index) in tableFields"
            :key="'dashboardPrecariageHeader' + index"
            @click="toggleSort(header.name)">
            <span
              v-if="findSort(sortedFields, header.name)"
              :class="findSort(sortedFields, header.name).direction"
              class="order-arrow" />
            {{ header.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(report, reportIndex) in reportList" :key="'reportTable' + reportIndex">
          <td v-if="report.User_Name === userName" @click="toggleDeleteReport(report)">
            <img src="../../../assets/icon-close-red.svg">
          </td>
          <td v-if="report.User_Name !== userName" @click="showAlert">
            <img src="../../../assets/icon-close.svg">
          </td>
          <td
            v-for="(field, fieldIndex) in tableFields"
            :key="'dashboardPrecariageField' + reportIndex + '.' + fieldIndex"
            :class="{'spl-parcel-table__field--number': isNumber(report[field.name]),
                     'spl-parcel-table__field--long': field.long,
                     'spl-parcel-table__field--bold': field.bold}"
            @click="reportSelected(report)">
            <span>
              {{ formatField(report[field.name], field.name, field.type) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="isLoading" class="spl-load-img" />
  </div>
</template>

<script lang="ts">
import { mapActions, mapGetters } from 'vuex'
import { find, findIndex, orderBy, isNumber } from 'lodash'
import { ReportListItem } from '@/webApi/DataModels'
import constants from '@/constants'
import Helpers from '@/helpers.vue'
export default {
  mixins: [Helpers],
  data () {
    return {
      tableFields: [
        { name: 'Report_Name', label: 'Report', type: 'String', long: true },
        { name: 'Report_Description', label: 'Report Description', type: 'String', long: true },
        { name: 'Latest_Update', label: 'Latest Update', type: 'Time' },
        { name: 'User_Name', label: 'User', type: 'String' },
        { name: 'Private', label: 'Private', type: 'Boolean' },
        { name: 'Customer_Name', label: 'Customer', type: 'String' },
        { name: 'Country_Name', label: 'Country', type: 'String' },
        { name: 'Base_On_Problem_Report', label: 'Based on Problem Reports', type: 'Boolean' }
      ],
      sortedFields: [],
      isLoading: false
    }
  },
  computed: {
    ...mapGetters('report', ['reportList']),
    ...mapGetters('user', ['userName'])
  },
  created () {
    this.isLoading = true
    this.loadReportList().then(() => {
      this.isLoading = false
    }).catch((error) => {
      window.bus.$emit('openErrorModal', error.ExceptionMessage)
      this.isLoading = false
    })
  },
  methods: {
    ...mapActions('report', ['loadReportList', 'sortList', 'deleteReport']),
    toggleDeleteReport (report) {
      this.isLoading = true
      // saving the report to the database
      this.deleteReport(report.Report_ID).then((response) => {
        this.isLoading = false
        window.bus.$emit('openSuccessModal', 'The report was successfully deleted!')
      }).catch((error) => {
        this.isLoading = false
        window.bus.$emit('openErrorModal', error.ExceptionMessage)
      })
    },
    showAlert () {
      window.bus.$emit('openWarningModal', 'Only the owner can delete this report!')
    },
    toggleSort (field) {
      // Finds the sorted field in the array
      var sortedField:any = find(this.sortedFields, { field: field })
      if (sortedField) {
        // Switches to desc when asc already selected
        if (sortedField.direction === 'asc') {
          sortedField.direction = 'desc'
        } else {
          // removes the sorting when desc was selected
          this.sortedFields.splice(findIndex(this.sortedFields, { field: field }))
        }
      } else {
        // adds the field when not yet present
        this.sortedFields.push({ direction: 'asc', field: field })
      }
      this.sortData()
    },
    reportSelected (report:ReportListItem) {
      this.$router.push('report-detail/'+ report.Report_ID)
    },
    // Sorting the data
    sortData () {
      // Sort fields and directions seperate for lodash orderBy method
      let sortFields = []
      let sortDirections = []
      // Create the sort fields
      for (let i = 0; i < this.sortedFields.length; i++) {
        const sortElement = this.sortedFields[i]
        sortFields.push(sortElement.field)
        sortDirections.push(sortElement.direction)
      }
      // Default sorting depending on grouping
      if (sortFields.length === 0) {
        sortFields = [constants.REFERENCE_TYPE_NAME.COUNTER_NUMBER]
        sortDirections = ['asc']
      }
      // Use the lodash orderBy method for sorting
      this.sortList({ sortFields, sortDirections })
    },
    // Special formatting for fields
    formatField (field, name, type) {
      if (type === 'Date') {
        return this.formatDate(field, false, 'YYYY-MM-DD')
      } else if (type === 'Time') {
        return this.formatDate(field, true, 'YYYY-MM-DD HH:mm')
      } else if (type === 'Boolean') {
        return field ? 'Yes' : 'No'
      }
      // empty fields will be removed
      if (field == null || field === 0) {
        return ''
      }
      return field
    },
    // find the right sort value for displaying arrows
    findSort (array, value) {
      return find(array, { field: value })
    },
    // including of helper since lodash not available in vue template
    isNumber (value) {
      return isNumber(value)
    }
  }

}
</script>

<style lang="scss">
  @import '../../../variables.scss';
  .spl-report-list-table {
    margin: 0 70px;
    width: calc(100% - 138px);

    table {
      border-spacing: 0;
      table-layout: fixed;
    }
    &__head {
      th {
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 3px 5px 0 rgba(0, 0, 0, 0.1);
      }
      &--sticky {
        position: sticky;
        left: 41px;
        z-index: 3 !important;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 10px 2px 0 6px;
        }
        position: sticky;
        left: 0px;
        z-index: 3 !important;
      }
    }
    &__row {
      &--selected {
        background-color: $tableRowBackgroundColorSelected !important;
      }
      &--deleted {
        opacity: 0.3;
        background-color: lightgrey !important;
      }
    }
    &__field {
      &--number {
        text-align: right;
        width: 70px;
      }
      &--sticky {
        position: sticky;
        left: 41px;
        box-shadow: 3px 0px 3px 0px rgba(0, 0, 0, 0.2);
        background-color: $tableHeaderBackgroundColor;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 0 2px 0 6px;
        }
        position: sticky;
        background-color: $tableHeaderBackgroundColor;
        left: 0;
      }
      &--long {
        max-width: 400px !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &--success {
        border-bottom: 2px solid $green !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $green;
      }
      &--warning {
        border-bottom: 2px solid $darkYellow !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $darkYellow;
      }
      &--error {
        border-bottom: 2px solid $red !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $red;
      }
      &--bold {
        font-weight: 600;
      }
      &--empty:hover {
        background-color: $tableRowBackgroundColorHover !important;
      }
    }
    thead {
      th {
        position: sticky;
        top: 0;
        text-align: left;
        font-family: $mono;
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        line-height: 12px;
        background-color: $tableHeaderBackgroundColor;
        padding: 0px 5px  0px 5px;
        height: 40px;
        border-bottom: 1px solid $tableHeaderBorderColor;
      }

      th:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }
    }
    tbody {

      tr {
        background-color: $tableRowBackgroundColor;
      }

      tr:hover {
        background-color: $tableRowBackgroundColorHover;
      }

      td {
        border-style: solid;
        border-width: 0 1px 1px 0;
        border-color: $tableRowBorderLowColor;
        border-bottom-color: $tableRowBorderColor;
        font-size: 12px;
        padding: 5px;
        padding-top: 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 150px;
      }

      td:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }

      td:first-child {
        border-left-width: 1px;
      }
    }
}
</style>
